import { useState } from "react";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import { API, waLink, postEvent, CONTACT_PHONE, CONTACT_EMAIL } from "@/lib/site";
import { Seo, PageHeader } from "@/components/common";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Loader2 } from "lucide-react";

export default function Contact() {
  const { t } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      toast.error(t({ tr: "Lütfen ad ve geçerli bir e-posta girin", en: "Please enter your name and a valid email" }));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { source: "contact", ...form, consent: false });
      postEvent("contact_submitted", { pref: "contact_form" });
      setSent(true);
    } catch {
      toast.error(t({ tr: "Gönderim başarısız oldu, lütfen tekrar deneyin", en: "Submission failed, please try again" }));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo title={t({ tr: "İletişim", en: "Contact" })} description={t({ tr: "Zeta Clinic iletişim: WhatsApp, e-posta ve form ile sağlık koordinatörümüze ulaşın.", en: "Zeta Clinic contact: reach our health coordinator via WhatsApp, email or the form." })} />
      <PageHeader
        tag={{ tr: "İletişim", en: "Contact" }}
        title={{ tr: "Sağlık koordinatörünüzle konuşun", en: "Talk to your health coordinator" }}
        subtitle={{ tr: "Mesajlarınıza 24 saat içinde, tercih ettiğiniz dilde yanıt veriyoruz.", en: "We respond to your messages within 24 hours, in your preferred language." }}
      />
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-8 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card" onClick={() => postEvent("whatsapp_clicked", { from: "contact" })} className="flex items-center gap-4 rounded-xl glass-card p-6 hover:shadow-lg transition-shadow">
            <div className="w-11 h-11 rounded-[10px] bg-[#25D366]/10 flex items-center justify-center"><MessageCircle size={20} className="text-[#25D366]" /></div>
            <div>
              <h2 className="font-heading font-semibold text-[#F2F5F7] text-sm">WhatsApp</h2>
              <p className="text-sm text-[#94A3AE]">{CONTACT_PHONE}</p>
            </div>
          </a>
          <div className="flex items-center gap-4 rounded-xl glass-card p-6">
            <div className="w-11 h-11 rounded-[10px] bg-[var(--accent-soft)] flex items-center justify-center"><Mail size={20} className="text-[var(--accent)]" /></div>
            <div>
              <h2 className="font-heading font-semibold text-[#F2F5F7] text-sm">E-posta</h2>
              <p className="text-sm text-[#94A3AE]">{CONTACT_EMAIL}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl glass-card p-6">
            <div className="w-11 h-11 rounded-[10px] bg-[var(--accent-soft)] flex items-center justify-center"><MapPin size={20} className="text-[var(--accent)]" /></div>
            <div>
              <h2 className="font-heading font-semibold text-[#F2F5F7] text-sm">{t({ tr: "Adres", en: "Address" })}</h2>
              <p className="text-sm text-[#94A3AE]">{t({ tr: "[Klinik adresi] · İstanbul", en: "[Clinic address] · Istanbul" })}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 rounded-2xl glass-card p-8 md:p-10">
          {sent ? (
            <div className="text-center py-12" data-testid="contact-success">
              <h2 className="font-heading text-xl font-semibold text-[#F2F5F7]">{t({ tr: "Mesajınız alındı", en: "Your message was received" })}</h2>
              <p className="text-sm text-[#94A3AE] mt-3">{t({ tr: "Sağlık koordinatörümüz 24 saat içinde size dönecek.", en: "Our health coordinator will get back to you within 24 hours." })}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t({ tr: "Ad Soyad", en: "Full name" })} className="rounded-[10px] border border-white/15 px-4 py-3 text-sm w-full" />
                <input data-testid="contact-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-posta" className="rounded-[10px] border border-white/15 px-4 py-3 text-sm w-full" />
              </div>
              <input data-testid="contact-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t({ tr: "Telefon (ülke koduyla)", en: "Phone (with country code)" })} className="rounded-[10px] border border-white/15 px-4 py-3 text-sm w-full" />
              <textarea data-testid="contact-form-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t({ tr: "Mesajınız", en: "Your message" })} rows={5} className="rounded-[10px] border border-white/15 px-4 py-3 text-sm w-full resize-none" />
              <button type="submit" disabled={sending} data-testid="contact-form-submit" className="inline-flex items-center gap-2 rounded-[10px] bg-[var(--accent)] text-white px-8 py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40">
                {sending && <Loader2 size={15} className="animate-spin" />}
                {t({ tr: "Gönder", en: "Send" })}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
